import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import {
  Title, Card, Table, Button, Group, Text, Modal,
  NumberInput, TextInput, Textarea, Grid, Stack, Loader, Center,
  ActionIcon, Tooltip, Badge, Divider,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconPlus, IconPencil, IconTrash, IconFileText,
  IconCircleCheck, IconCircleDashed,
} from '@tabler/icons-react';
import 'dayjs/locale/it';

function formatAmount(v) {
  if (!v && v !== 0) return '—';
  return '€ ' + Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatMese(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
}

function meseToStr(d) {
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function ReconcIcon({ items, fieldRef, onToggle }) {
  const checked = (items || []).some(i => i.field_ref === fieldRef);
  return (
    <Tooltip label={checked ? 'Riconciliato manualmente' : 'Segna come riconciliato'}>
      <ActionIcon variant="subtle" size="sm" onClick={() => onToggle(fieldRef)} style={{ marginLeft: 4 }}>
        {checked
          ? <IconCircleCheck size={16} color="#2b8a3e" />
          : <IconCircleDashed size={16} color="#adb5bd" />
        }
      </ActionIcon>
    </Tooltip>
  );
}

const EMPTY_FORM = {
  mese_riferimento: null,
  marca: '', modello: '',
  societa_noleggio: '',
  acquirente_denominazione: '',
  provvigioni_attive_sistema: 0,
  provvigioni_attive_nette_perc: 0,
  note: '',
  riconciliazioni: [],
};

export default function NoleggioV2() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroMese, setFiltroMese] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  useEffect(() => { loadRecords(); }, [filtroMese]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroMese) params.mese = meseToStr(filtroMese);
      const res = await api.get('/noleggio-v2', { params });
      setRecords(res.data);
    } catch {
      notifications.show({ title: 'Errore', message: 'Impossibile caricare i dati', color: 'red' });
    } finally { setLoading(false); }
  };

  const resetForm = () => { setForm({ ...EMPTY_FORM }); setEditingRecord(null); };

  const openNew = () => { resetForm(); setShowModal(true); };

  const openEdit = (r) => {
    setEditingRecord(r);
    setForm({
      mese_riferimento: r.mese_riferimento ? new Date(r.mese_riferimento) : null,
      marca: r.marca || '', modello: r.modello || '',
      societa_noleggio: r.societa_noleggio || '',
      acquirente_denominazione: r.acquirente_denominazione || '',
      provvigioni_attive_sistema: r.provvigioni_attive_sistema || 0,
      provvigioni_attive_nette_perc: r.provvigioni_attive_nette_perc || 0,
      note: r.note || '',
      riconciliazioni: r.riconciliazioni || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Eliminare il noleggio ${r.marca || ''} ${r.modello || ''} (${formatMese(r.mese_riferimento)})?`)) return;
    try {
      await api.delete(`/noleggio-v2/${r.id}`);
      notifications.show({ title: 'Eliminato', message: 'Record eliminato', color: 'green' });
      loadRecords();
    } catch {
      notifications.show({ title: 'Errore', message: "Errore nell'eliminazione", color: 'red' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.mese_riferimento) {
      notifications.show({ title: 'Attenzione', message: 'Seleziona il mese di riferimento', color: 'orange' });
      return;
    }
    try {
      const payload = {
        ...form,
        mese_riferimento: meseToStr(form.mese_riferimento),
        marca: form.marca || null,
        modello: form.modello || null,
        societa_noleggio: form.societa_noleggio || null,
        acquirente_denominazione: form.acquirente_denominazione || null,
        note: form.note || null,
      };
      if (editingRecord) {
        await api.put(`/noleggio-v2/${editingRecord.id}`, payload);
        notifications.show({ title: 'Salvato', message: 'Noleggio aggiornato', color: 'green' });
      } else {
        await api.post('/noleggio-v2', payload);
        notifications.show({ title: 'Creato', message: 'Noleggio aggiunto', color: 'green' });
      }
      setShowModal(false);
      resetForm();
      loadRecords();
    } catch (err) {
      notifications.show({ title: 'Errore', message: err.response?.data?.detail || 'Errore nel salvataggio', color: 'red' });
    }
  };

  const toggleReconc = (fieldRef) => {
    setForm(prev => {
      const already = (prev.riconciliazioni || []).some(i => i.field_ref === fieldRef);
      const others = (prev.riconciliazioni || []).filter(i => i.field_ref !== fieldRef);
      return { ...prev, riconciliazioni: already ? others : [...others, { field_ref: fieldRef, tipo: 'manuale' }] };
    });
  };

  // Calcolo provvigioni nette in-form (preview)
  const calcNette = (f) => {
    return ((f.provvigioni_attive_sistema || 0) * (f.provvigioni_attive_nette_perc || 0) / 100);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm">
          <IconFileText size={28} color="#205357" stroke={1.5} />
          <Title order={2} style={{ color: '#205357' }}>Noleggio</Title>
        </Group>
        <Button leftSection={<IconPlus size={16} />} color="teal" onClick={openNew} fullWidth={isMobile}>
          Nuovo noleggio
        </Button>
      </Group>

      <Card shadow="sm" radius="md" withBorder>
        <Group mb="md" w="100%">
          <MonthPickerInput
            placeholder="Filtra per mese"
            value={filtroMese}
            onChange={setFiltroMese}
            clearable
            size="sm"
            locale="it"
            style={{ flex: 1, minWidth: 160 }}
          />
          {filtroMese && (
            <Button variant="subtle" size="xs" color="gray" onClick={() => setFiltroMese(null)}>
              Rimuovi filtro
            </Button>
          )}
        </Group>

        {loading ? (
          <Center h={200}><Loader /></Center>
        ) : records.length === 0 ? (
          <Center h={100}><Text c="dimmed">Nessun noleggio trovato</Text></Center>
        ) : (
          <Table.ScrollContainer minWidth={isMobile ? 0 : 900}>
          <Table striped highlightOnHover withTableBorder withColumnBorders style={{ fontSize: isMobile ? '0.8rem' : 13 }}>
            <Table.Thead>
              <Table.Tr>
                {!isMobile && <Table.Th>Mese</Table.Th>}
                <Table.Th>Veicolo</Table.Th>
                {!isMobile && <Table.Th>Società noleggio</Table.Th>}
                <Table.Th>Acquirente</Table.Th>
                {!isMobile && <Table.Th style={{ textAlign: 'right' }}>Provv. sistema</Table.Th>}
                {!isMobile && <Table.Th style={{ textAlign: 'right' }}>% nette</Table.Th>}
                <Table.Th style={{ textAlign: 'right' }}>Provv. nette</Table.Th>
                {!isMobile && <Table.Th>Riconciliazioni</Table.Th>}
                <Table.Th w={isMobile ? 60 : 80}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {records.map(r => {
                const ricLinked = (r.riconciliazioni || []).length;
                return (
                  <Table.Tr key={r.id} onClick={() => isMobile && openEdit(r)} style={isMobile ? { cursor: 'pointer' } : undefined}>
                    {!isMobile && <Table.Td>{formatMese(r.mese_riferimento)}</Table.Td>}
                    <Table.Td>
                      <Text size={isMobile ? 'xs' : 'sm'}>{[r.marca, r.modello].filter(Boolean).join(' ') || '—'}</Text>
                    </Table.Td>
                    {!isMobile && <Table.Td>{r.societa_noleggio || '—'}</Table.Td>}
                    <Table.Td style={isMobile ? { maxWidth: 100 } : undefined}>
                      <Text size={isMobile ? 'xs' : 'sm'} truncate={isMobile ? 'end' : undefined}>
                        {r.acquirente_denominazione || '—'}
                      </Text>
                    </Table.Td>
                    {!isMobile && <Table.Td style={{ textAlign: 'right' }}>{formatAmount(r.provvigioni_attive_sistema)}</Table.Td>}
                    {!isMobile && (
                      <Table.Td style={{ textAlign: 'right' }}>
                        {r.provvigioni_attive_nette_perc ? `${r.provvigioni_attive_nette_perc}%` : '—'}
                      </Table.Td>
                    )}
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size={isMobile ? 'xs' : 'sm'} fw={600} c="teal">{formatAmount(r.provvigioni_attive_nette)}</Text>
                      {isMobile && ricLinked > 0 && (
                        <Badge size="xs" color="teal" variant="light" mt={2}>{ricLinked} fatt.</Badge>
                      )}
                    </Table.Td>
                    {!isMobile && (
                      <Table.Td>
                        {ricLinked > 0
                          ? <Badge size="xs" color="teal" variant="light">{ricLinked} fattura/e</Badge>
                          : <Text size="xs" c="dimmed">—</Text>
                        }
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Tooltip label="Modifica">
                          <ActionIcon variant="subtle" color="blue" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); openEdit(r); }}>
                            <IconPencil size={isMobile ? 14 : 16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Elimina">
                          <ActionIcon variant="subtle" color="red" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); handleDelete(r); }}>
                            <IconTrash size={isMobile ? 14 : 16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      {/* Modal form */}
      <Modal
        opened={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={<Text fw={600}>{editingRecord ? 'Modifica noleggio' : 'Nuovo noleggio'}</Text>}
        size={isMobile ? '100%' : 'lg'}
        fullScreen={isMobile}
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="sm">
            <MonthPickerInput
              label="Mese di riferimento"
              placeholder="Seleziona mese"
              value={form.mese_riferimento}
              onChange={v => setForm(f => ({ ...f, mese_riferimento: v }))}
              required
              locale="it"
            />

            <Divider label="Veicolo" labelPosition="left" />
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Marca" value={form.marca} onChange={e => setForm(f => ({ ...f, marca: e.target.value }))} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Modello" value={form.modello} onChange={e => setForm(f => ({ ...f, modello: e.target.value }))} />
              </Grid.Col>
            </Grid>

            <Divider label="Anagrafica" labelPosition="left" />
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Società di noleggio" value={form.societa_noleggio} onChange={e => setForm(f => ({ ...f, societa_noleggio: e.target.value }))} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Acquirente (denominazione)" value={form.acquirente_denominazione} onChange={e => setForm(f => ({ ...f, acquirente_denominazione: e.target.value }))} />
              </Grid.Col>
            </Grid>

            <Divider label="Provvigioni (IVA esclusa)" labelPosition="left" />
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Group gap={4} align="flex-end">
                  <NumberInput
                    label="Provvigioni attive a sistema"
                    value={form.provvigioni_attive_sistema}
                    onChange={v => setForm(f => ({ ...f, provvigioni_attive_sistema: v || 0 }))}
                    prefix="€ " decimalScale={2} hideControls thousandSeparator="."
                    decimalSeparator=","
                    style={{ flex: 1 }}
                  />
                  <ReconcIcon
                    items={form.riconciliazioni}
                    fieldRef="provvigioni_attive_sistema"
                    onToggle={toggleReconc}
                  />
                </Group>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label="% provvigioni attive nette"
                  value={form.provvigioni_attive_nette_perc}
                  onChange={v => setForm(f => ({ ...f, provvigioni_attive_nette_perc: v || 0 }))}
                  suffix="%" decimalScale={2} hideControls min={0} max={100}
                />
              </Grid.Col>
            </Grid>

            {/* Preview provvigioni nette */}
            <Card withBorder radius="sm" p="xs" style={{ backgroundColor: '#f8f9fa' }}>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Provvigioni nette calcolate</Text>
                <Text fw={700} c="teal">{formatAmount(calcNette(form))}</Text>
              </Group>
              {form.provvigioni_attive_nette_perc > 0 && form.provvigioni_attive_sistema > 0 && (
                <Text size="xs" c="dimmed">
                  {formatAmount(form.provvigioni_attive_sistema)} × {form.provvigioni_attive_nette_perc}%
                </Text>
              )}
            </Card>

            <Divider label="Note" labelPosition="left" />
            <Textarea
              placeholder="Note aggiuntive..."
              value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              rows={3}
            />

            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => { setShowModal(false); resetForm(); }}>Annulla</Button>
              <Button type="submit" color="teal">{editingRecord ? 'Aggiorna' : 'Salva'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
